import {
  Flex,
  IconButton,
  Input,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import './Combobox.scss';
import {
  DropdownOption,
  groupOptionsByCategory,
  isDropdownOptionsArrayOfObjects,
} from '@/utils/formatDropdownOptions';

interface ComboboxProps {
  value: string;
  onBlur: () => void;
  onChange: (option: string) => void;
  dropdownOptions: string[] | DropdownOption[];
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
}

export const Combobox = ({
  value,
  onBlur,
  onChange,
  dropdownOptions,
  placeholder = 'Select an option',
  isRequired = false,
  isDisabled = false,
}: ComboboxProps) => {
  // Flatten options for useCombobox
  const flattenedOptions = useMemo(() => {
    if (isDropdownOptionsArrayOfObjects(dropdownOptions)) {
      return dropdownOptions.map(({ option }) => option);
    }
    return dropdownOptions;
  }, [dropdownOptions]);

  const [dropdownItems, setDropdownItems] = useState(flattenedOptions);
  const [hasInput, setHasInput] = useState(false);

  const getFilteredItems = (inputValue: string) => {
    return flattenedOptions.filter(item =>
      item.toLowerCase().startsWith(inputValue.toLowerCase())
    );
  };

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    getToggleButtonProps,
    highlightedIndex,
  } = useCombobox({
    items: dropdownItems,
    onInputValueChange: ({ inputValue = '' }) => {
      setHasInput(inputValue.length > 0);
      setDropdownItems(getFilteredItems(inputValue));
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem ?? '');
    },
    itemToString: item => item ?? '',
    selectedItem: value,
  });

  ///////////////////////////////////////
  // Wrapped in useMemo to avoid unnecessary recalculations on every render.
  ///////////////////////////////////////

  const groupedOptions = useMemo(
    () =>
      isDropdownOptionsArrayOfObjects(dropdownOptions)
        ? groupOptionsByCategory(dropdownOptions)
        : {},
    [dropdownOptions]
  );

  ///////////////////////////////////////
  // Render logic
  ///////////////////////////////////////

  const isActiveClassName = (itemIndex: number) => {
    return itemIndex === highlightedIndex ? 'active' : '';
  };

  const renderGroupedOptions = () => {
    let itemIndex = 0; // Initialize a separate item index

    return Object.entries(groupedOptions).map(([category, options]) => (
      <React.Fragment key={category}>
        <Text className="combobox-list-category">{category}</Text>

        {options.map((option: string) => {
          const currentIndex = itemIndex; // Capture the current value of itemIndex for this iteration
          itemIndex++; // Increment itemIndex for the next item

          return (
            <ListItem
              {...getItemProps({ item: option, index: currentIndex })}
              key={option}
              className={`combobox-list-item ${isActiveClassName(
                currentIndex
              )}`}
            >
              {option}
            </ListItem>
          );
        })}
      </React.Fragment>
    ));
  };

  const renderItems = (items: string[]) =>
    items.map((item, index) => (
      <ListItem
        {...getItemProps({ item, index })}
        key={item}
        className={`combobox-list-item ${isActiveClassName(index)}`}
      >
        {item}
      </ListItem>
    ));

  const renderListItemsComponent = () => {
    if (isDropdownOptionsArrayOfObjects(dropdownOptions)) {
      return hasInput ? renderItems(dropdownItems) : renderGroupedOptions();
    }

    return renderItems(dropdownItems);
  };

  const handleBlur = () => {
    const inputValue = getInputProps().value;
    if (!inputValue) {
      onChange('');
    }
    onBlur();
  };

  return (
    <Flex className="combobox-container">
      <Input
        {...getInputProps({ onBlur: handleBlur })}
        placeholder={placeholder}
        isRequired={isRequired}
        isDisabled={isDisabled}
      />

      <IconButton
        {...getToggleButtonProps()}
        className="combobox-toggle-button"
        aria-label="toggle dropdown list"
        icon={isOpen ? <ChevronUp /> : <ChevronDown />}
      />

      <List {...getMenuProps()} className={`combobox-list`}>
        {isOpen && renderListItemsComponent()}
      </List>
    </Flex>
  );
};

export default Combobox;
